import { useState, useRef, useEffect, useCallback } from 'react';
import { Clock, X, Calendar, ChevronDown } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import {
  getSchedulePresets,
  isValidScheduleDate,
  formatScheduleDate,
  toDateInputValue,
  toTimeInputValue,
  getMinScheduleDate,
  getMaxScheduleDate,
} from '@/lib/utils/schedule';

interface SchedulePickerPopoverProps {
  scheduledAt: Date | null;
  onSchedule: (date: Date | null) => void;
  /** Popover alignment relative to trigger */
  align?: 'start' | 'end';
  /** Which side the popover opens on */
  side?: 'top' | 'bottom';
  /** Compact mode — smaller trigger for chat input */
  compact?: boolean;
}

export function SchedulePickerPopover({
  scheduledAt,
  onSchedule,
  align = 'end',
  side = 'top',
  compact = false,
}: SchedulePickerPopoverProps) {
  const [open, setOpen] = useState(false);
  const [showCustom, setShowCustom] = useState(false);
  const [customDate, setCustomDate] = useState('');
  const [customTime, setCustomTime] = useState('');
  const [error, setError] = useState('');
  const containerRef = useRef<HTMLDivElement>(null);
  const popoverRef = useRef<HTMLDivElement>(null);

  // Close on outside click
  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  // Clamp popover position within viewport after render
  const clampToViewport = useCallback(() => {
    const el = popoverRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    // Prevent overflow above viewport
    if (rect.top < 4) {
      el.style.bottom = 'auto';
      el.style.top = '0px';
    }
    // Prevent overflow on the right
    if (rect.right > window.innerWidth - 4) {
      el.style.right = '0px';
      el.style.left = 'auto';
    }
    // Prevent overflow on the left
    if (rect.left < 4) {
      el.style.left = '0px';
      el.style.right = 'auto';
    }
  }, []);

  useEffect(() => {
    if (open) requestAnimationFrame(clampToViewport);
  }, [open, clampToViewport]);

  // Pre-fill custom inputs when opening with an existing schedule
  useEffect(() => {
    if (open && scheduledAt) {
      setCustomDate(toDateInputValue(scheduledAt));
      setCustomTime(toTimeInputValue(scheduledAt));
      setShowCustom(false);
    } else if (open) {
      const defaultDate = new Date(Date.now() + 60 * 60 * 1000);
      setCustomDate(toDateInputValue(defaultDate));
      setCustomTime(toTimeInputValue(defaultDate));
      setShowCustom(false);
    }
    setError('');
  }, [open, scheduledAt]);

  const handlePreset = (getDate: () => Date) => {
    const date = getDate();
    if (!isValidScheduleDate(date)) {
      setError('This time is too soon. Must be at least 15 min from now.');
      return;
    }
    onSchedule(date);
    setOpen(false);
  };

  const handleCustomSchedule = () => {
    if (!customDate || !customTime) {
      setError('Please select both date and time.');
      return;
    }
    const [year, month, day] = customDate.split('-').map(Number);
    const [hours, minutes] = customTime.split(':').map(Number);
    const date = new Date(year, month - 1, day, hours, minutes, 0, 0);

    if (!isValidScheduleDate(date)) {
      const min = getMinScheduleDate();
      const max = getMaxScheduleDate();
      if (date.getTime() < min.getTime()) {
        setError('Must be at least 15 minutes from now.');
      } else if (date.getTime() > max.getTime()) {
        setError('Cannot schedule more than 35 days ahead.');
      } else {
        setError('Invalid schedule time.');
      }
      return;
    }

    onSchedule(date);
    setOpen(false);
  };

  const handleClear = () => {
    onSchedule(null);
    setOpen(false);
  };

  const presets = getSchedulePresets();
  const minDate = toDateInputValue(new Date());
  const maxDate = toDateInputValue(getMaxScheduleDate());

  return (
    <div ref={containerRef} className="relative">
      {/* Trigger */}
      {compact ? (
        <button
          type="button"
          onClick={() => setOpen(!open)}
          className={cn(
            'rounded-lg p-1.5 transition-colors',
            scheduledAt
              ? 'text-primary bg-primary/10'
              : 'text-muted-foreground hover:bg-muted hover:text-foreground'
          )}
          title={scheduledAt ? `Scheduled: ${formatScheduleDate(scheduledAt)}` : 'Schedule message'}
        >
          <Clock className="h-4 w-4" />
        </button>
      ) : (
        <Button
          type="button"
          variant="default"
          size="icon"
          data-testid="schedule-picker-trigger"
          onClick={() => setOpen(!open)}
          className="rounded-l-none border-l border-l-primary-foreground/20 h-full"
        >
          <ChevronDown className={cn('h-4 w-4 transition-transform', open && 'rotate-180')} />
        </Button>
      )}

      {/* Popover */}
      {open && (
        <div
          data-testid="schedule-dropdown"
          ref={popoverRef}
          className={cn(
            'absolute z-50 w-72 max-w-[calc(100vw-1rem)] rounded-lg border border-border bg-popover shadow-lg animate-in fade-in-0 zoom-in-95',
            side === 'top' && 'bottom-full mb-2',
            side === 'bottom' && 'top-full mt-2',
            align === 'end' && 'right-0',
            align === 'start' && 'left-0'
          )}
        >
          {/* Header */}
          <div className="flex items-center justify-between px-3 py-2.5 border-b border-border">
            <span className="text-sm font-medium text-foreground">Schedule Message</span>
            <button
              type="button"
              onClick={() => setOpen(false)}
              className="rounded p-0.5 text-muted-foreground hover:text-foreground transition-colors"
            >
              <X className="h-3.5 w-3.5" />
            </button>
          </div>

          {/* Presets */}
          <div data-testid="schedule-options" className="p-1.5 space-y-0.5">
            {presets.map((preset) => (
              <button
                key={preset.label}
                type="button"
                onClick={() => handlePreset(preset.getDate)}
                className="flex items-center gap-2.5 w-full rounded-md px-2.5 py-2 text-sm text-foreground hover:bg-accent transition-colors text-left"
              >
                <Clock className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
                <span>{preset.label}</span>
                <span className="ml-auto text-xs text-muted-foreground">
                  {formatScheduleDate(preset.getDate())}
                </span>
              </button>
            ))}
          </div>

          {/* Custom date/time */}
          <div className="border-t border-border">
            <button
              type="button"
              onClick={() => setShowCustom(!showCustom)}
              className="flex items-center gap-2 w-full px-3 py-2 text-sm text-foreground hover:bg-accent transition-colors"
            >
              <Calendar className="h-3.5 w-3.5 text-muted-foreground" />
              <span>Custom date & time</span>
              <ChevronDown
                className={cn(
                  'h-3.5 w-3.5 ml-auto text-muted-foreground transition-transform',
                  showCustom && 'rotate-180'
                )}
              />
            </button>

            {showCustom && (
              <div className="px-3 pb-3 space-y-2">
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <label className="text-[10px] font-medium text-muted-foreground uppercase tracking-wide">
                      Date
                    </label>
                    <input
                      type="date"
                      value={customDate}
                      min={minDate}
                      max={maxDate}
                      onChange={(e) => { setCustomDate(e.target.value); setError(''); }}
                      className="w-full rounded-lg border border-input bg-background px-2 py-1.5 text-xs focus:outline-none focus:ring-1 focus:ring-ring"
                    />
                  </div>
                  <div>
                    <label className="text-[10px] font-medium text-muted-foreground uppercase tracking-wide">
                      Time
                    </label>
                    <input
                      type="time"
                      value={customTime}
                      onChange={(e) => { setCustomTime(e.target.value); setError(''); }}
                      className="w-full rounded-lg border border-input bg-background px-2 py-1.5 text-xs focus:outline-none focus:ring-1 focus:ring-ring"
                    />
                  </div>
                </div>
                <Button
                  type="button"
                  size="sm"
                  className="w-full"
                  onClick={handleCustomSchedule}
                >
                  <Clock className="h-3.5 w-3.5" />
                  Set schedule
                </Button>
                {error && (
                  <p className="text-[11px] text-destructive">{error}</p>
                )}
              </div>
            )}
          </div>

          {/* Clear schedule — only when active */}
          {scheduledAt && (
            <div className="border-t border-border p-1.5">
              <button
                type="button"
                onClick={handleClear}
                className="flex items-center gap-2 w-full rounded-md px-2.5 py-2 text-sm text-destructive hover:bg-destructive/10 transition-colors"
              >
                <X className="h-3.5 w-3.5" />
                <span>Clear schedule</span>
                <span className="ml-auto text-xs text-muted-foreground">
                  {formatScheduleDate(scheduledAt)}
                </span>
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
